import * as React from 'react';
import { useState, useEffect } from 'react';
import UserGroupService, { User, Group, WorkArea, UpdateUserGroupsRequest, UpdateWorkAreaAccessRequest, BulkUpdateRequest } from '../../../services/permissionService';
import { toast } from 'react-toastify';
import { ChangeGroupsModal } from './ChangeGroupsModal';
import { ChangeWorkAreaModal } from './ChangeWorkAreaModal';
import { BulkOperationsModal } from './BulkOperationsModal';
import styles from './AdminOverview.module.css';

function AdminOverview(): React.JSX.Element {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [availableWorkAreas, setAvailableWorkAreas] = useState<WorkArea[]>([]);

  const [showChangeGroupsModal, setShowChangeGroupsModal] = useState<boolean>(false);
  const [showChangeWorkAreaModal, setShowChangeWorkAreaModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalLoading, setModalLoading] = useState<boolean>(false);

  const [showBulkModal, setShowBulkModal] = useState<boolean>(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [bulkHasUnrestrictedAccess, setBulkHasUnrestrictedAccess] = useState<boolean>(false);
  const [bulkSelectedWorkAreaIds, setBulkSelectedWorkAreaIds] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);

  const [openActionMenuFor, setOpenActionMenuFor] = useState<string | null>(null);

  useEffect(() => {
    loadAllData();
  }, []);

  async function loadAllData(): Promise<void> {
    try {
      const [fetchedUsers, groups, workAreas] = await Promise.all([
        UserGroupService.fetchAllUsers(),
        UserGroupService.fetchAllGroups(),
        UserGroupService.fetchAllWorkAreas()
      ]);
      const usersWithGroups = fetchedUsers.filter(user => user.groups.length > 0);
      setUsers(usersWithGroups);
      setAvailableUsers(fetchedUsers);
      setAvailableGroups(groups);
      setAvailableWorkAreas(workAreas);
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message);
      setLoading(false);
    }
  }

  function formatWorkAreaDisplay(user: User): string {
    if (user.has_unrestricted_work_area_access) return 'ALL';
    if (user.work_area_restrictions.length === 0) return 'None';
    return user.work_area_restrictions.map(area => area.name).join(', ');
  }

  function toggleActionMenu(userKey: string): void {
    setOpenActionMenuFor(openActionMenuFor === userKey ? null : userKey);
  }

  function handleOpenChangeGroups(userId: number): void {
    setOpenActionMenuFor(null);
    const user = availableUsers.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowChangeGroupsModal(true);
    }
  }

  function handleOpenChangeWorkArea(userId: number): void {
    setOpenActionMenuFor(null);
    const user = availableUsers.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setShowChangeWorkAreaModal(true);
    }
  }

  async function handleUpdateUserGroups(groupIds: number[]): Promise<void> {
    if (!selectedUser) return;
    setModalLoading(true);
    try {
      const request: UpdateUserGroupsRequest = { group_ids: groupIds };
      await UserGroupService.updateUserGroups(selectedUser.id, request);
      await loadAllData();
      setShowChangeGroupsModal(false);
      setSelectedUser(null);
    } catch (err: any) {
      console.error('Error updating user groups:', err);
      toast.error('Error updating user groups. Please try again.');
    } finally {
      setModalLoading(false);
    }
  }

  async function handleUpdateUserWorkArea(hasUnrestrictedAccess: boolean, workAreaIds: number[]): Promise<void> {
    if (!selectedUser) return;
    setModalLoading(true);
    try {
      const request: UpdateWorkAreaAccessRequest = {
        has_unrestricted_work_area_access: hasUnrestrictedAccess,
        work_area_restriction_ids: hasUnrestrictedAccess ? [] : workAreaIds
      };
      await UserGroupService.updateUserWorkAreaAccess(selectedUser.id, request);
      await loadAllData();
      setShowChangeWorkAreaModal(false);
      setSelectedUser(null);
    } catch (err: any) {
      console.error('Error updating user work area access:', err);
      toast.error('Error updating user work area access. Please try again.');
    } finally {
      setModalLoading(false);
    }
  }

  async function handleBulkUpdate(): Promise<void> {
    if (selectedUserIds.length === 0) {
      toast.warning('Please select at least one user.');
      return;
    }
    setBulkLoading(true);
    try {
      const request: BulkUpdateRequest = {
        user_ids: selectedUserIds,
        group_ids: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
        has_unrestricted_work_area_access: bulkHasUnrestrictedAccess,
        work_area_restriction_ids: bulkHasUnrestrictedAccess ? [] : bulkSelectedWorkAreaIds
      };
      await UserGroupService.bulkUpdate(request);
      await loadAllData();
      handleCloseBulkModal();
    } catch (err: any) {
      console.error('Error performing bulk update:', err);
      toast.error('Error performing bulk update. Please try again.');
    } finally {
      setBulkLoading(false);
    }
  }

  function handleCloseBulkModal(): void {
    setShowBulkModal(false);
    setSelectedUserIds([]);
    setSelectedGroupIds([]);
    setBulkHasUnrestrictedAccess(false);
    setBulkSelectedWorkAreaIds([]);
  }

  React.useEffect(() => {
    function handleClickOutside(): void {
      if (openActionMenuFor) setOpenActionMenuFor(null);
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openActionMenuFor]);

  const sortedUsers = [...users].sort((a, b) => {
    const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
    const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const renderActionMenu = (user: User) => {
    if (openActionMenuFor !== `user-${user.id}`) return null;
    return (
      <div className={styles.actionMenu}>
        <div className={styles.actionMenuHeader}>
          <span className={styles.actionMenuTitle}>Actions</span>
          <button onClick={() => setOpenActionMenuFor(null)} className={styles.actionMenuClose} title="Close">
            &times;
          </button>
        </div>
        <div className={styles.actionMenuItem} onClick={() => handleOpenChangeGroups(user.id)}>
          Change Groups
        </div>
        <div className={styles.actionMenuItem} onClick={() => handleOpenChangeWorkArea(user.id)}>
          Change Work Area
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}><p>Loading users...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}><p>Error loading users: {error}</p></div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.page}>
        <div className={styles.contentWrapper}>
          <div className={styles.headerRow}>
            <h2 className={styles.title}>Permissions</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button onClick={() => setShowBulkModal(true)} className={styles.addButton}>
                Hinzufügen
              </button>
            </div>
          </div>

          <div className={styles.userList}>
            <div className={styles.columnHeaders}>
              <div className={styles.colUser}><span className={styles.columnHeaderText}>User</span></div>
              <div className={styles.colGroups}><span className={styles.columnHeaderText}>Groups</span></div>
              <div className={styles.colWorkArea}><span className={styles.columnHeaderText}>Work Area</span></div>
              <div className={styles.colActions}><span className={styles.columnHeaderText}>Actions</span></div>
            </div>

            {sortedUsers.map((user) => {
              const workAreaDisplay = formatWorkAreaDisplay(user);
              return (
                <div key={user.id} className={styles.userCard}>
                  <div className={styles.colUser}>
                    <div className={styles.userName}>{user.first_name} {user.last_name}</div>
                    <div className={styles.userEmail}>{user.email}</div>
                  </div>

                  <div className={styles.colGroups}>
                    <div className={styles.groupBadges}>
                      {user.groups.map(group => (
                        <span key={group.id} className={styles.groupBadge}>{group.name}</span>
                      ))}
                      {user.groups.length === 0 && (
                        <span className={styles.noGroups}>No groups assigned</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.colWorkArea}>
                    <span className={`${styles.workAreaBadge} ${workAreaDisplay === 'ALL' ? styles.workAreaAll : styles.workAreaRestricted}`}>
                      {workAreaDisplay}
                    </span>
                  </div>

                  <div className={styles.actionsCol} onClick={e => e.stopPropagation()}>
                    <button
                      className={styles.kebabButton}
                      onClick={() => toggleActionMenu(`user-${user.id}`)}
                      title="Actions"
                    >
                      &#8942;
                    </button>
                    {renderActionMenu(user)}
                  </div>
                </div>
              );
            })}

            {sortedUsers.length === 0 && (
              <div className={styles.emptyState}>No users found.</div>
            )}
          </div>
        </div>
      </div>

      {showChangeGroupsModal && selectedUser && (
        <ChangeGroupsModal
          user={selectedUser}
          availableGroups={availableGroups}
          onUpdate={handleUpdateUserGroups}
          onClose={() => { setShowChangeGroupsModal(false); setSelectedUser(null); }}
          loading={modalLoading}
        />
      )}

      {showChangeWorkAreaModal && selectedUser && (
        <ChangeWorkAreaModal
          user={selectedUser}
          availableWorkAreas={availableWorkAreas}
          onUpdate={handleUpdateUserWorkArea}
          onClose={() => { setShowChangeWorkAreaModal(false); setSelectedUser(null); }}
          loading={modalLoading}
        />
      )}

      {showBulkModal && (
        <BulkOperationsModal
          availableUsers={availableUsers}
          availableGroups={availableGroups}
          availableWorkAreas={availableWorkAreas}
          selectedUserIds={selectedUserIds}
          selectedGroupIds={selectedGroupIds}
          hasUnrestrictedAccess={bulkHasUnrestrictedAccess}
          selectedWorkAreaIds={bulkSelectedWorkAreaIds}
          onUserIdsChange={setSelectedUserIds}
          onGroupIdsChange={setSelectedGroupIds}
          onUnrestrictedAccessChange={setBulkHasUnrestrictedAccess}
          onWorkAreaIdsChange={setBulkSelectedWorkAreaIds}
          onUpdate={handleBulkUpdate}
          onClose={handleCloseBulkModal}
          loading={bulkLoading}
        />
      )}
    </>
  );
}

export default AdminOverview;
